// src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { StoreProvider, useStore } from '../context/StoreContext';
import CategoryList from '../components/CategoryList';
import AddItemForm from '../components/AddItemForm';
import ItemList from '../components/ItemList';
import OrderManagement from '../components/OrderManagement';
import { fetchCategories } from '../config/api';
import { Flame } from 'lucide-react';

function DashboardContent() {
  const { categories, setInitialCategories } = useStore();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState('orders'); // 'menu' or 'orders'

  const loadCategories = useCallback(async () => {
    try {
      const data = await fetchCategories();
      setInitialCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [setInitialCategories]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-orange-100">
    {/* Header */}
    <div className="px-4 py-10 text-white shadow-lg bg-gradient-to-r from-amber-800 to-amber-900">
      <div className="flex items-center justify-center mx-auto max-w-7xl">
        <div className="flex items-center space-x-3">
          <Flame size={48} className="text-orange-400" />
          <h1 className="text-5xl tracking-tight font-bebas">Mr WoodFire</h1>
        </div>
      </div>
    </div>

    {/* Tabs */}
    <div className="px-4 mx-auto max-w-7xl">
      <div className="flex gap-4 mt-8 mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 text-lg font-medium rounded-lg transition-colors
              ${activeTab === 'orders'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-amber-600 hover:bg-amber-50'
              }`}
          >
            Order Management
          </button>
        <button
          onClick={() => setActiveTab('menu')}
          className={`px-6 py-3 text-lg font-medium rounded-lg transition-colors
            ${activeTab === 'menu'
              ? 'bg-amber-500 text-white'
              : 'bg-white text-amber-600 hover:bg-amber-50'
            }`}
        >
          Menu Management
        </button>
      </div>
    </div>

    {/* Main Content */}
    <div className="px-4 py-8 mx-auto max-w-7xl">
      {activeTab === 'menu' ? (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Sidebar */}
          <div className="space-y-8 lg:col-span-4">
            <CategoryList />
            <div className="p-6 bg-white shadow-md rounded-xl">
              <h2 className="mb-4 text-xl font-semibold text-amber-900 font-bebas">
                Filter by Category
              </h2>
              <select
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full p-3 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="space-y-8 lg:col-span-8">
            <AddItemForm />
            <ItemList selectedCategory={selectedCategory} />
          </div>
        </div>
      ) : (
        <OrderManagement />
      )}
    </div>
  </div>
);
}

// Wrap the dashboard content with the provider
function Dashboard() {
  return (
    <StoreProvider>
      <DashboardContent />
    </StoreProvider>
  );
}

export default Dashboard;
